import { Injectable } from '@angular/core';
import { PlaidConfig, PlaidErrorMetadata, PlaidErrorObject, PlaidSuccessMetadata } from './interfaces';
import { PlaidLinkHandler } from './ngx-plaid-link-handler';
import { FundingAccessTokenCreateRequest, FundingControllerService, FundingLinkTokenCreateRequest } from 'skyhopper-api';
import { FacadeService } from 'src/app/shared/service-facade/facade.service';
import { BASK_BRAND_NAME, SUCCESS_PATH, TCB_BRAND_NAME } from 'src/app/shared/constants';
import { CoreService } from 'src/app/shared/services/core.service';
import { environment } from 'src/environments/environment';
import { SectionStatusService } from 'src/app/status-tracker/section-status.service';
import { Sections, SectionStates } from 'src/app/status-tracker/model/section-status';

@Injectable()
export class NgxPlaidLinkService {
  private loaded: Promise<void> | undefined;
  private plaidLinkHandler: PlaidLinkHandler | undefined;
  private sessionId: string;

  constructor(private fundingControllerService: FundingControllerService,
    private facadeService: FacadeService,
    private coreService: CoreService,
    private statusTrackerService: SectionStatusService
  ) {
    this.sessionId = this.facadeService.getSessionId();
   }

  /**
   * Create a Plaid Link instance as soon as Plaid Link has loaded.
   * @param PlaidConfig config
   * @returns Promise<PlaidLinkHandler>
   */
  public createPlaid(config: PlaidConfig): Promise<PlaidLinkHandler> {
    return this.loadPlaid().then(() => {
      return new PlaidLinkHandler(config);
    });
  }

  /**
   * Load or wait for the Plaid Link library to load.
   * @returns Promise<void>
   */
  public loadPlaid(): Promise<void> {
    if (!this.loaded) {
      this.loaded = new Promise<void>((resolve, reject) => {
        const script: any = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
        script.onerror = (e: any) => reject(e);
        if (script.readyState) {
          script.onreadystatechange = () => {
            if (script.readyState === 'loaded' || script.readyState === 'complete') {
              script.onreadystatechange = null;
              resolve();
            }
          };
        } else {
          script.onload = () => {
            resolve();
          };
        }
        document.getElementsByTagName('body')[0].appendChild(script);
      });
    }

    return this.loaded;
  }

  async openPlaidPopup() {
    let fundingLinkTokenCreateRequest: FundingLinkTokenCreateRequest = {} as FundingLinkTokenCreateRequest;

    fundingLinkTokenCreateRequest.applicationId = this.sessionId
    fundingLinkTokenCreateRequest.brandName = this.facadeService.isBaskBrand()? BASK_BRAND_NAME : TCB_BRAND_NAME

    let fundingTokenCreateResponse: any = null;
    try {
      fundingTokenCreateResponse = await this.fundingControllerService.createLinkedToken(fundingLinkTokenCreateRequest).toPromise();
    } catch (error) {
      if(!environment.production) {
        console.error('Error creating funding token:', error);
      }
      this.facadeService.markStopActionsLoading();
    }

    if(!fundingTokenCreateResponse) {
      this.facadeService.markStopActionsLoading();
    }

    let token = fundingTokenCreateResponse['link_token'];

    let handler: PlaidLinkHandler = await this.createPlaid({
        onSuccess: (publicToken: string, metadata: PlaidSuccessMetadata) => this.onAccountLinkSuccess(publicToken, metadata),
        onExit: (err: PlaidErrorObject, metadata: PlaidErrorMetadata) => this.onExit(err, metadata),
        apiVersion: "v2",
        env: environment.production? "" : "sandbox",
        selectAccount: false,
        token: token,
        webhook: "",
        product: ["auth"],
        countryCodes: ["US"],
      });

      this.plaidLinkHandler = handler;

      handler.open();
  }

  onExit(err: PlaidErrorObject, metadata: PlaidErrorMetadata) {
    if(!environment.production) {
      console.log(metadata);
    }

    if(err) {
      console.error(err);
    }

    this.plaidLinkHandler?.exit();
    this.facadeService.markStopActionsLoading();
  }


  async onAccountLinkSuccess(publicToken: string, metadata: PlaidSuccessMetadata) {
    let fundingAccessTokenCreateRequest: FundingAccessTokenCreateRequest = {} as FundingAccessTokenCreateRequest;
    fundingAccessTokenCreateRequest.applicationId = this.sessionId;
    fundingAccessTokenCreateRequest.publicToken = publicToken

    //TODO: (628214) - Utilize below for funding alocations.
    let fundingAccessTokenCreateResponse: any = await this.fundingControllerService.createAccessToken(fundingAccessTokenCreateRequest).toPromise();
    if(!environment.production) {
      console.log(metadata);
      console.log(fundingAccessTokenCreateResponse);
    }

    //TODO: (628214) - Replace with appropraite routing.
    // this.statusTrackerService.updateStatusTrackerSectionStatus(Sections.FundAccounts, SectionStates.complete);
    this.coreService.navigate(`/${SUCCESS_PATH}`);
  }

}
